import { mergeRefs } from "@/client/react";
import { Rating } from "@/util/fsrs";
import type { PropsOf } from "@pinyinly/lib/types";
import { useLayoutEffect, useRef } from "react";
import type { ViewInstance } from "react-native";
import z from "zod";
import { ratingToThemeName } from "./QuizDeckResultToast.utils";
import { RectButton } from "./RectButton";
import { Theme } from "./Theme";

const quizSubmitButtonStateSchema = z.enum({
  Disabled: `Disabled`,
  Check: `Check`,
  Correct: `Correct`,
  Incorrect: `Incorrect`,
});

export const QuizSubmitButtonState = quizSubmitButtonStateSchema.enum;
export type QuizSubmitButtonState = z.infer<typeof quizSubmitButtonStateSchema>;

interface QuizSubmitButtonProps extends Pick<
  PropsOf<typeof RectButton>,
  `onPress` | `ref`
> {
  autoFocus?: boolean;
  isChecking?: boolean;
  isUserAnswerProvided: boolean;
  rating: Rating | null | undefined;
}

export const QuizSubmitButton = ({
  autoFocus = false,
  isChecking = false,
  isUserAnswerProvided,
  rating,
  onPress,
  ref,
}: QuizSubmitButtonProps) => {
  const disabled = rating == null && !isUserAnswerProvided;

  let text;

  if (isChecking) {
    text = `Checking`;
  } else {
    switch (rating) {
      case undefined:
      case null: {
        text = `Check`;
        break;
      }
      case Rating.Easy:
      case Rating.Good:
      case Rating.Hard: {
        text = `Continue`;
        break;
      }
      case Rating.Again: {
        text = `Got it`;
        break;
      }
    }
  }

  const buttonRef = useRef<ViewInstance>(null);

  useLayoutEffect(() => {
    if (autoFocus && buttonRef.current != null) {
      buttonRef.current.focus();
    }
  }, [autoFocus, buttonRef]);

  return (
    <Theme
      theme={
        disabled
          ? undefined
          : rating == null
            ? `success-panel`
            : ratingToThemeName(rating)
      }
    >
      <RectButton
        variant="filled"
        ref={mergeRefs(buttonRef, ref)}
        disabled={disabled || isChecking}
        className={`flex-1`}
        onPress={disabled || isChecking ? undefined : onPress}
      >
        {text}
      </RectButton>
    </Theme>
  );
};
